import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { FolderOpen, Mail, Trash2 } from '../components/icons';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { IconPill } from '../components/IconPill';
import { FeatureHeader } from '../components/FeatureHeader';
import { ScreenHeader } from '../components/ScreenHeader';
import { DecorBlob } from '../components/DecorBlob';
import { ContenuLtr } from '../components/ContenuLtr';
import { useGabarit } from '../hooks/useGabarit';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import { listCoverLetters, deleteCoverLetter } from '../services/coverLetter';
import { formatSavedDate } from '../utils/cvDates';
import type { SavedCoverLetter } from '../types';
import {
  colonnesGrille,
  colors,
  layout,
  radius,
  screenPadding,
  shadow,
  spacing,
  typography,
} from '../theme/tokens';
import { t } from '../i18n';
import { confirmer } from '../utils/confirmation';
import { BLOC_LTR, TEXTE_LTR, citation } from '../i18n/sens';

export interface CoverLetterListProps {
  onOpenLetter: (letter: SavedCoverLetter) => void;
  onCreate: () => void;
  onBack?: () => void;
}

type LoadState = 'loading' | 'error' | 'ready';

const ACCENT = FEATURE_ACCENTS.letter;

// Ligne de la liste sur ordinateur : un clic choisit la lettre, l'aperçu s'affiche à droite.
function LigneLettre({ letter, choisie, onPress }: { letter: SavedCoverLetter; choisie: boolean; onPress: () => void }) {
  const [survol, setSurvol] = useState(false);
  return (
    <Pressable
      testID={`letterlist-card-${letter.id}`}
      accessibilityRole="button"
      accessibilityState={{ selected: choisie }}
      accessibilityLabel={`${letter.title}, ${formatSavedDate(letter.created_at)}`}
      onPress={onPress}
      onHoverIn={() => setSurvol(true)}
      onHoverOut={() => setSurvol(false)}
      style={[styles.ligne, survol && styles.ligneSurvol, choisie && styles.ligneChoisie]}
    >
      <IconPill icon={Mail} accent="letter" variant="soft" size={40} />
      <View style={styles.cardTexts}>
        <View style={styles.titleRow}>
          <Text style={styles.cardTitle} numberOfLines={2}>
            {letter.title}
          </Text>
          {letter.output_language === 'en' ? (
            <View testID={`letterlist-lang-${letter.id}`} style={styles.langBadge}>
              <Text style={styles.langBadgeText}>EN</Text>
            </View>
          ) : null}
        </View>
        <Text style={styles.cardDate}>{formatSavedDate(letter.created_at)}</Text>
      </View>
    </Pressable>
  );
}


/**
 * « Mes lettres » — liste des lettres enregistrées, calquée sur CvListScreen.
 *
 * La suppression unitaire est le seul levier réel sur les données du destinataire (§ 2.2 de la
 * politique) : elle est donc offerte ici, avec confirmation, et non reléguée à la suppression du
 * compte. Il n'y a pas de notion de lettre « principale » — contrairement aux CV, rien ne
 * s'affiche ailleurs dans l'application à partir d'une lettre.
 */
export function CoverLetterListScreen({ onOpenLetter, onCreate, onBack }: CoverLetterListProps) {
  // Même décision que le fil News, prise au même endroit : seul le MÉCANISME de rendu diffère
  // (ici un `.map` dans une ScrollView, là-bas `numColumns`). Vaut 1 sur appareil, par construction.
  const { width: largeurFenetre } = useWindowDimensions();
  const colonnes = colonnesGrille(
    Math.min(largeurFenetre, layout.contentMaxWidth) - screenPadding * 2,
  );
  const [letters, setLetters] = useState<SavedCoverLetter[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const { large } = useGabarit();
  const [selId, setSelId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLetters(await listCoverLetters());
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmDelete = (letter: SavedCoverLetter) => {
    confirmer(t('letterList.deleteTitle'), t('letterList.deleteBody', { title: citation(letter.title) }), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteCoverLetter(letter.id);
            await load();
          } catch {
            setState('error');
          }
        },
      },
    ]);
  };

  const choisie = letters.find((l) => l.id === selId) ?? letters[0];

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <DecorBlob />
      <ScreenHeader onBack={onBack} testID="letterlist-back" />
      <View style={[styles.header, large && styles.headerL]}>
        <FeatureHeader
          icon={FolderOpen}
          accent="letter"
          title={t('letterList.title')}
          subtitle={t('letterList.subtitle')}
        />
      </View>

      {state === 'loading' ? (
        <View style={styles.center}>
          <ActivityIndicator testID="letterlist-loading" color={colors.corail} />
        </View>
      ) : null}

      {state === 'error' ? (
        <View style={styles.center}>
          <Text testID="letterlist-error" style={styles.error}>
            {t('letterList.loadError')}
          </Text>
          <Button title={t('common.retry')} variant="secondary" onPress={() => void load()} testID="letterlist-retry" />
        </View>
      ) : null}

      {state === 'ready' && letters.length === 0 ? (
        <View testID="letterlist-empty" style={styles.center}>
          <Text style={styles.emptyTitle}>{t('letterList.emptyTitle')}</Text>
          <Text style={styles.emptyText}>{t('letterList.emptyText')}</Text>
          <Button title={t('letterList.emptyCta')} withArrow onPress={onCreate} testID="letterlist-empty-create" />
        </View>
      ) : null}

      {state === 'ready' && letters.length > 0 && large && choisie ? (
        <View testID="letterlist-deux-volets" style={styles.deuxVolets}>
          <ScrollView style={styles.volet} contentContainerStyle={styles.listeL} showsVerticalScrollIndicator={false}>
            {letters.map((letter) => (
              <LigneLettre key={letter.id} letter={letter} choisie={letter.id === choisie.id} onPress={() => setSelId(letter.id)} />
            ))}
          </ScrollView>

          <View style={styles.fiche}>
            <View style={styles.barreFiche}>
              <View style={styles.boutonOuvrir}>
                <Button title={t('letterList.open')} withArrow onPress={() => onOpenLetter(choisie)} testID={`letterlist-open-${choisie.id}`} />
              </View>
              <Pressable
                testID={`letterlist-delete-${choisie.id}`}
                accessibilityRole="button"
                accessibilityLabel={t('common.delete')}
                onPress={() => confirmDelete(choisie)}
                style={styles.supprimer}
              >
                <Trash2 size={18} color={colors.error} strokeWidth={2} />
                <Text style={styles.supprimerTexte}>{t('common.delete')}</Text>
              </Pressable>
            </View>
            <ScrollView style={styles.feuille} contentContainerStyle={styles.feuilleContenu} showsVerticalScrollIndicator={false}>
              <ContenuLtr>
                <Text style={styles.feuilleObjet}>{choisie.content.objet}</Text>
                <View style={BLOC_LTR}>
                  <Text style={styles.feuilleTexte}>{choisie.content.lettre.formule_appel}</Text>
                  {choisie.content.lettre.paragraphes.map((para, i) => (
                    <Text key={i} style={styles.feuilleTexte}>
                      {para}
                    </Text>
                  ))}
                  <Text style={styles.feuilleTexte}>{choisie.content.lettre.formule_politesse}</Text>
                  <Text style={[styles.feuilleTexte, styles.feuilleSignature]}>{choisie.content.lettre.signature}</Text>
                </View>
              </ContenuLtr>
            </ScrollView>
          </View>
        </View>
      ) : null}

      {state === 'ready' && letters.length > 0 && !large ? (
        <ScrollView contentContainerStyle={[styles.list, colonnes > 1 && styles.grille]}>
          {letters.map((letter) => (
            <Card key={letter.id} style={colonnes > 1 ? styles.cellule : undefined}>
              <Pressable
                testID={`letterlist-card-${letter.id}`}
                accessibilityRole="button"
                onPress={() => onOpenLetter(letter)}
                style={styles.cardMain}
              >
                <IconPill icon={Mail} accent="letter" variant="soft" size={40} />
                <View style={styles.cardTexts}>
                  <View style={styles.titleRow}>
                    <Text style={styles.cardTitle} numberOfLines={1}>
                      {letter.title}
                    </Text>
                    {/* Pastille sur les documents NON français seulement : la marquer sur tout
                        alourdirait une liste où le français est le cas courant. */}
                    {letter.output_language === 'en' ? (
                      <View testID={`letterlist-lang-${letter.id}`} style={styles.langBadge}>
                        <Text style={styles.langBadgeText}>EN</Text>
                      </View>
                    ) : null}
                  </View>
                  <Text style={styles.cardDate}>{formatSavedDate(letter.created_at)}</Text>
                  {/* Le texte d'une lettre importée en PDF est une TRANSCRIPTION du modèle, pas
                      l'original : le dire ici évite que la personne découvre seule un écart avec
                      son fichier, longtemps après l'analyse. */}
                  {letter.content?.texte_reconstitue ? (
                    <Text style={styles.cardRebuilt} testID={`letterlist-rebuilt-${letter.id}`}>
                      {t('letterAnalyse.rebuiltFromPdf')}
                    </Text>
                  ) : null}
                </View>
              </Pressable>

              <View style={styles.actions}>
                <Pressable
                  testID={`letterlist-delete-${letter.id}`}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.delete')}
                  onPress={() => confirmDelete(letter)}
                  hitSlop={8}
                  style={styles.deleteBtn}
                >
                  <Trash2 size={20} color={colors.grey500} strokeWidth={2} />
                </Pressable>
              </View>
            </Card>
          ))}
        </ScrollView>
      ) : null}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  headerL: {
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    paddingHorizontal: spacing.xxl + spacing.xl,
  },
  deuxVolets: {
    flex: 1,
    flexDirection: 'row',
    gap: spacing.xl,
    minHeight: 0,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    paddingHorizontal: spacing.xxl + spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
  },
  volet: { width: layout.voletPrincipalLargeur, flexGrow: 0, flexShrink: 0 },
  listeL: { gap: spacing.sm },
  ligne: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: 'transparent',
    cursor: 'pointer',
    ...shadow.card,
  },
  ligneSurvol: { borderColor: colors.bordureClaire },
  ligneChoisie: { borderColor: ACCENT.ink, backgroundColor: ACCENT.tint },
  fiche: { flex: 1, minWidth: 0, gap: spacing.md },
  barreFiche: { flexDirection: 'row', alignItems: 'center', gap: spacing.lg },
  boutonOuvrir: { minWidth: 200 },
  supprimer: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, minHeight: 44, cursor: 'pointer' },
  supprimerTexte: { fontSize: typography.small.size, fontWeight: '800', color: colors.error },
  feuille: { flex: 1, borderRadius: radius.lg, backgroundColor: colors.white, ...shadow.card },
  feuilleContenu: { padding: spacing.xxl, gap: spacing.md },
  feuilleObjet: { ...TEXTE_LTR, fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.night, marginBottom: spacing.sm },
  feuilleTexte: {
    ...TEXTE_LTR,
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey700,
    marginBottom: spacing.sm,
  },
  feuilleSignature: { fontWeight: '700', color: colors.grey900 },
  container: { flex: 1, backgroundColor: colors.grey50 },
  header: { paddingHorizontal: screenPadding, paddingTop: spacing.lg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: screenPadding, gap: spacing.md },
  list: { padding: screenPadding, gap: spacing.md },
  // Grille : les cellules s'enroulent d'elles-mêmes dès qu'une largeur minimale ne tient plus.
  grille: { flexDirection: 'row', flexWrap: 'wrap' },
  cellule: { flexGrow: 1, flexBasis: 0, minWidth: layout.gridMinItemWidth },
  cardMain: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  cardTexts: { flex: 1, gap: spacing.xs },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  cardRebuilt: { fontSize: typography.caption.size, color: colors.grey500 },
  langBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radius.pill,
    backgroundColor: colors.blueSoft,
  },
  langBadgeText: { fontSize: typography.caption.size, fontWeight: '700', color: colors.electric },
  cardTitle: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  cardDate: { fontSize: typography.small.size, color: colors.grey500 },
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginTop: spacing.sm },
  deleteBtn: { padding: spacing.xs },
  emptyTitle: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.night, textAlign: 'center' },
  emptyText: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
    textAlign: 'center',
  },
  error: { fontSize: typography.body.size, color: colors.error, textAlign: 'center' },
});
